import { useState } from "react";
import { storage, db } from "../../../lib/firebase";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { useAuth } from "../../auth/context/AuthContext";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import type { AdequacaoAmostral, AchadosExplanteExtras } from "../../../types";
interface UploadProgress {
  progress: number;
  uploading: boolean;
  error: string | null;
}

// ✅ Interface atualizada com achados da IA
interface ExameImagem {
  id: string;
  pacienteId: string;
  pacienteNome: string;
  tipo: string;
  descricao: string;
  indicacaoClinica?: string;
  dataExame: string;
  nomeArquivo: string;
  tamanho: number;
  url: string;
  uploadPor: string;
  uploadPorNome: string;
  criadoEm: string;
  caminhoStorage?: string;
  // ✅ NOVOS CAMPOS - Achados da IA
  achados?: AchadosExame;
  resumoAchados?: string;
  analiseIAEm?: string;
}

// ✅ Interface para achados estruturados da IA
// ✅ B28.2pre (AI_50) — herda AchadosExplanteExtras, mirror da canônica em extrairAchadosIA.ts
export interface AchadosExame extends AchadosExplanteExtras {
  tipo: string;
  data: string;
  lesoesHepaticas: string;
  statusMilao: "DENTRO" | "FORA" | "NÃO APLICÁVEL" | "";
  trombosePortal: string;
  tipoTrombose: "BLAND" | "TUMORAL" | "NÃO APLICÁVEL" | "";
  shunts: string;
  ascite: "SIM" | "NÃO" | "";
  esplenomegalia: string;
  outrosAchados: string;
  conclusao: string;
  // ✅ Bloco 1 AI_34 — transcrição literal do laudista (Haiku vision)
  conclusaoLaudistaLiteral?: string;
  tipoLaudista?:
    | "patologista"
    | "radiologista"
    | "endoscopista"
    | "ecografista"
    | "cardiologista"
    | "outro";
  conclusaoLiteralGeradaEm?: string;
  conclusaoLiteralStatus?: "sucesso" | "sem_secao_conclusao" | "erro_extracao";

  // ✅ AI_40 Patch 2 Alt 4.2 — adequação amostral (só biópsias)
  adequacaoAmostral?: AdequacaoAmostral;
}
export function useUploadExame() {
  const { usuarioLogado } = useAuth();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    progress: 0,
    uploading: false,
    error: null,
  });

  // ============================================
  // 📤 FAZER UPLOAD DE EXAME
  // ============================================
  const uploadExame = async (
    file: File,
    pacienteId: string,
    pacienteNome: string,
    metadata: {
      tipo: string;
      descricao: string;
      dataExame: string;
      indicacaoClinica?: string;
    }
  ): Promise<ExameImagem | null> => {
    if (!usuarioLogado) {
      setUploadProgress({
        progress: 0,
        uploading: false,
        error: "Usuário não autenticado",
      });
      return null;
    }

    // Validar tipo de arquivo
    const tiposPermitidos = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/dicom",
      // ✅ VÍDEOS
      "video/mp4",
      "video/quicktime", // .mov
      "video/x-msvideo", // .avi
      "video/x-matroska", // .mkv
    ];

    if (!tiposPermitidos.includes(file.type)) {
      setUploadProgress({
        progress: 0,
        uploading: false,
        error:
          "Tipo de arquivo não permitido. Use JPG, PNG, PDF, DICOM, MP4, MOV, AVI ou MKV.",
      });
      return null;
    }

    // Validar tamanho (máximo 500MB)
    const tamanhoMaximo = 500 * 1024 * 1024; // 500MB
    if (file.size > tamanhoMaximo) {
      setUploadProgress({
        progress: 0,
        uploading: false,
        error: "Arquivo muito grande. Máximo: 500MB",
      });
      return null;
    }

    try {
      setUploadProgress({ progress: 0, uploading: true, error: null });

      // Criar nome único para o arquivo
      const timestamp = Date.now();
      const nomeArquivo = `${timestamp}_${file.name}`;
      const caminho = `exames-imagem/${pacienteId}/${nomeArquivo}`;

      // Criar referência no Storage
      const storageRef = ref(storage, caminho);

      // Fazer upload com progresso
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // Atualizar progresso
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress({
              progress: Math.round(progress),
              uploading: true,
              error: null,
            });
          },
          (error) => {
            // Erro no upload
            console.error("❌ Erro no upload:", error);
            setUploadProgress({
              progress: 0,
              uploading: false,
              error: error.message,
            });
            reject(error);
          },
          async () => {
            // Upload concluído com sucesso
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              // Salvar metadados no Firestore
              const exameDoc = await addDoc(collection(db, "exames-imagem"), {
                pacienteId,
                pacienteNome,
                tipo: metadata.tipo,
                descricao: metadata.descricao,
                indicacaoClinica: metadata.indicacaoClinica || "",
                dataExame: metadata.dataExame,
                nomeArquivo: file.name,
                nomeArquivoStorage: nomeArquivo,
                caminhoStorage: caminho,
                tamanho: file.size,
                tipoArquivo: file.type,
                url: downloadURL,
                uploadPor: usuarioLogado.id,
                uploadPorNome: usuarioLogado.nome,
                criadoEm: serverTimestamp(),
              });

              const exameImagem: ExameImagem = {
                id: exameDoc.id,
                pacienteId,
                pacienteNome,
                tipo: metadata.tipo,
                descricao: metadata.descricao,
                dataExame: metadata.dataExame,
                nomeArquivo: file.name,
                tamanho: file.size,
                url: downloadURL,
                uploadPor: usuarioLogado.id,
                uploadPorNome: usuarioLogado.nome,
                criadoEm: new Date().toISOString(),
              };

              setUploadProgress({
                progress: 100,
                uploading: false,
                error: null,
              });
              // B318-FU (AI_143, M5): não despejar o objeto (contém
              // pacienteNome/descrição) — só o id do exame criado.
              console.log("✅ Upload concluído:", exameImagem.id);
              resolve(exameImagem);
            } catch (error: any) {
              console.error("❌ Erro ao salvar metadados:", error);
              setUploadProgress({
                progress: 0,
                uploading: false,
                error: error.message,
              });
              reject(error);
            }
          }
        );
      });
    } catch (error: any) {
      console.error("❌ Erro no upload:", error);
      setUploadProgress({
        progress: 0,
        uploading: false,
        error: error.message,
      });
      return null;
    }
  };

  // ============================================
  // 📥 BUSCAR EXAMES DO PACIENTE
  // ============================================
  const buscarExames = async (pacienteId: string): Promise<ExameImagem[]> => {
    try {
      console.log("🔍 Buscando exames do paciente:", pacienteId);

      const q = query(
        collection(db, "exames-imagem"),
        where("pacienteId", "==", pacienteId)
      );

      const querySnapshot = await getDocs(q);

      const exames: ExameImagem[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          pacienteId: data.pacienteId,
          pacienteNome: data.pacienteNome,
          tipo: data.tipo,
          descricao: data.descricao,
          indicacaoClinica: data.indicacaoClinica || "",
          dataExame: data.dataExame,
          nomeArquivo: data.nomeArquivo,
          tamanho: data.tamanho,
          url: data.url,
          uploadPor: data.uploadPor,
          uploadPorNome: data.uploadPorNome,
          criadoEm: data.criadoEm?.toDate?.()?.toISOString?.() || "",
          caminhoStorage: data.caminhoStorage || "",
          // ✅ NOVOS CAMPOS
          achados: data.achados || null,
          resumoAchados: data.resumoAchados || "",
          analiseCompleta: data.analiseCompleta || "",
          analiseIAEm: data.analiseIAEm?.toDate?.()?.toISOString?.() || "",
        };
      });

      console.log("✅ Exames encontrados:", exames.length);
      return exames;
    } catch (error) {
      console.error("❌ Erro ao buscar exames:", error);
      return [];
    }
  };

  // ============================================
  // 🗑️ DELETAR EXAME
  // ============================================
  const deletarExame = async (
    exameId: string,
    caminhoStorage: string
  ): Promise<boolean> => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não autenticado");
      return false;
    }

    try {
      console.log("🗑️ Deletando exame:", exameId);

      // B213 — lê o doc ANTES do delete p/ a lápide da trilha (este hook
      // não tem o estado do context em mãos)
      const snapAntes = await getDoc(doc(db, "exames-imagem", exameId));
      const dadosAntes: any = snapAntes.exists() ? snapAntes.data() : null;

      // Deletar arquivo do Storage
      const storageRef = ref(storage, caminhoStorage);
      await deleteObject(storageRef);

      // Deletar documento do Firestore
      await deleteDoc(doc(db, "exames-imagem", exameId));

      await registrarExclusaoTrilha({
        colecao: "exames-imagem",
        docId: exameId,
        pacienteId: dadosAntes?.pacienteId || "",
        rotulo: dadosAntes
          ? `${dadosAntes.tipoExame || "Imagem"}${
              dadosAntes.dataExame
                ? ` de ${new Date(dadosAntes.dataExame).toLocaleDateString(
                    "pt-BR"
                  )}`
                : ""
            }`
          : "Imagem",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
      });

      console.log("✅ Exame deletado com sucesso!");
      return true;
    } catch (error) {
      console.error("❌ Erro ao deletar exame:", error);
      return false;
    }
  };

  // ============================================
  // 🤖 SALVAR ACHADOS DA ANÁLISE IA
  // ============================================
  const salvarAchadosIA = async (
    exameId: string,
    achados: Partial<AchadosExame> | null,
    resumoAchados: string,
    analiseCompleta?: string
  ): Promise<boolean> => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não autenticado");
      return false;
    }

    try {
      console.log("🤖 Salvando achados da IA para exame:", exameId);

      const exameRef = doc(db, "exames-imagem", exameId);
      const snapAntes = await getDoc(exameRef);
      const dadosAntes: any = snapAntes.exists() ? snapAntes.data() : null;

      await updateDoc(exameRef, {
        achados: achados,
        resumoAchados: resumoAchados,
        analiseCompleta: analiseCompleta || "",
        analiseIAEm: serverTimestamp(),
        analiseIAPor: usuarioLogado.id,
        analiseIAPorNome: usuarioLogado.nome,
      });

      // B213 — gravação dos achados da IA é uma edição do registro
      await registrarEdicaoTrilha({
        colecao: "exames-imagem",
        docId: exameId,
        pacienteId: dadosAntes?.pacienteId || "",
        rotulo: dadosAntes
          ? `${dadosAntes.tipoExame || "Imagem"} — análise IA`
          : "Imagem — análise IA",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: ["achados", "resumoAchados", "analiseCompleta"],
      });

      console.log("✅ Achados salvos com sucesso!");
      return true;
    } catch (error) {
      console.error("❌ Erro ao salvar achados:", error);
      return false;
    }
  };

  return {
    uploadExame,
    buscarExames,
    deletarExame,
    salvarAchadosIA, // ✅ NOVA FUNÇÃO EXPORTADA
    uploadProgress,
  };
}
